import { needsBacklogGrant, type BacklogConnectionState } from '@/hooks/useBacklogConnection'
import { logActivity } from '@/services/activity'
import { CheckIcon, XMarkIcon } from '@/components/Icons'
import { fmt, type UiStrings } from '@/constants/ui-locale'
import { formatDate } from '@/utils/formatDateTime'
import { useStore } from '@/store/useStore'

/** One line of the capability list: what the grant does, or pointedly does not. */
function Capability({ allowed, children }: { allowed: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-xs leading-relaxed text-[var(--text-secondary)]">
      {allowed ? (
        <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />
      ) : (
        <XMarkIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
      )}
      <span className="min-w-0">{children}</span>
    </li>
  )
}

/**
 * The Backlog grant as a card: what it is for, whether it works, and the one
 * thing there is to do about it.
 *
 * Two faces, because the question changes once a grant exists. Before one, the
 * card is a consent screen: approving an OAuth application on the tracker
 * everybody's tickets live in is not a decision to make from a button labelled
 * "Connect" and nothing else, and Backlog's own consent page is the wrong
 * place to read the terms for the first time. After one, the terms are settled
 * and the open question is whose account it runs as — so the card names the
 * account and offers to end it.
 *
 * @param connection Shared grant state; the Sync button on a meeting reads the
 *   same hook, so this must not keep a copy of its own.
 */
export default function BacklogSettings({
  connection,
  t,
}: {
  connection: BacklogConnectionState
  t: UiStrings
}) {
  const locale = useStore((s) => s.uiLocale)
  const account = connection.connection?.account
  const space = connection.connection?.space
  const connectedAt = connection.connection?.connectedAt
  const connected = connection.connected
  const status = connection.connection?.status
  const needsGrant = needsBacklogGrant(status)
  const busy = connection.phase === 'saving'

  /** Navigating here rather than in the hook keeps the departure in the thing the reader pressed. */
  async function beginSso() {
    const url = await connection.startSso()
    // Counted here rather than on the way back, because the way back lands on
    // a different screen and a reader who abandons the approval still tried
    // (BRAI-677). A URL that never arrived is the failure.
    logActivity('backlog.connect_started', { status: url ? 'success' : 'failure' })
    if (url) window.location.assign(url)
  }

  /**
   * Asked before it is done: this gives up an authorization, and the next Push
   * is the moment the reader would otherwise find out.
   */
  async function endGrant() {
    if (!window.confirm(t.settingsBacklogDisconnectConfirm)) return
    await connection.disconnect()
  }

  return (
    <section className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {/* `flex-1`, so the title block shrinks and the pill keeps its place on
            this row. Without it the block sized to its description and pushed
            the pill onto a line of its own at every width — measured down to
            560px, where it still fits; below that wrapping is the right
            answer. */}
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-100 text-base font-bold text-green-700 dark:bg-green-500/15 dark:text-green-400"
          >
            B
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-[var(--text-primary)]">
              {t.settingsBacklogCardTitle}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">
              {connected ? t.settingsBacklogPushDescription : t.settingsBacklogDescription}
            </p>
          </div>
        </div>

        <span
          className={[
            'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold',
            connected
              ? 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-400'
              : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]',
          ].join(' ')}
        >
          <span
            aria-hidden
            className={[
              'h-1.5 w-1.5 rounded-full',
              connected ? 'bg-green-600' : 'bg-[var(--text-tertiary)]',
            ].join(' ')}
          />
          {connected ? t.settingsBacklogStatusConnected : t.settingsBacklogStatusNotConnected}
        </span>
      </div>

      {connected ? (
        <>
          {/* The account, not just the fact of one: the ticket will carry this
              name, and "Connected" alone does not say connected as whom. */}
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-[var(--accent)] bg-[var(--accent-light)] p-4">
            <span
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--bg-secondary)] text-sm font-bold text-[var(--accent)]"
            >
              {account?.name?.trim()?.[0]?.toUpperCase() ?? 'B'}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-[var(--text-primary)]">
                {account?.name ?? t.settingsBacklogCardTitle}
              </p>
              <p className="mt-0.5 truncate text-xs text-[var(--text-secondary)]">
                {[
                  space,
                  connectedAt
                    ? fmt(t.settingsBacklogConnectedSince, {
                        date: formatDate(connectedAt, locale),
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => void endGrant()}
            disabled={busy}
            className="mt-4 rounded-md border border-red-300 px-3.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-500/40 dark:hover:bg-red-500/10"
          >
            {busy ? t.settingsBacklogDisconnecting : t.settingsBacklogDisconnect}
          </button>
          <p className="mt-2 text-xs text-[var(--text-tertiary)]">
            {t.settingsBacklogDisconnectNote}
          </p>
        </>
      ) : (
        <>
          <div className="mt-4 rounded-lg bg-[var(--bg-tertiary)] p-4">
            <h3 className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-tertiary)]">
              {t.settingsBacklogCapabilities}
            </h3>
            <ul className="mt-2.5 space-y-2">
              <Capability allowed>{t.settingsBacklogCanProjects}</Capability>
              <Capability allowed>{t.settingsBacklogCanTickets}</Capability>
              <Capability allowed={false}>{t.settingsBacklogCannot}</Capability>
            </ul>
          </div>

          {needsGrant && (
            <div className="mt-4">
              {/* Only for an expired grant. "Not connected" is already on the
                  pill, but which space stopped taking the grant is not. */}
              {status === 'REAUTH_REQUIRED' && (
                <p className="mb-2 text-xs text-[var(--text-secondary)]">
                  {space
                    ? fmt(t.backlogConnectionExpired, { space })
                    : t.backlogConnectionSpaceUnknown}
                </p>
              )}
              <button
                type="button"
                onClick={() => void beginSso()}
                disabled={busy}
                className="btn-primary rounded-md px-3.5 py-2 text-xs font-semibold disabled:opacity-50"
              >
                {busy ? t.backlogConnecting : t.backlogConnectWithBacklog}
              </button>
              <p className="mt-2 text-xs text-[var(--text-tertiary)]">{t.backlogSsoHint}</p>
            </div>
          )}
        </>
      )}

      {connection.error && (
        <p role="alert" className="mt-3 text-xs text-red-600">
          {connection.error}
        </p>
      )}
    </section>
  )
}
